// The ONE place that knows which physical stock each printer holds, what to call it,
// and whether a given layout is allowed on it. Pure localStorage + a server copy
// (zebra.js reads THIS; keeps deps one-way, no import cycle).
//
// Config shape (server: app_settings.printer_config):
//   stocks  {printerUid: mediaKey}  — what each printer HOLDS (authoritative, operator-set)
//   map     {mediaKey: printerUid}  — which holder is the DEFAULT for that stock
//   names   {printerUid: nickname}  — what you call it
// Several printers MAY hold the same stock (a spare); exactly one of them is the default.
// Routing REFUSES on an unmapped media (no silent guess) and REFUSES to send a layout to
// a printer holding different stock (see mediaMismatch) — both at one chokepoint.

// Physical MEDIA/stock. This is NOT label category: a donor tag or a container label can
// print on EITHER 4x6 or 4x3, so every flow passes the chosen media, not the label type.
export const MEDIA_TYPES = [
  { key: "hang_tag", label: "Hang Tag (4×6)" },
  { key: "sticker", label: "Sticker (4×3)" },
  { key: "barcode_label", label: "Barcode Label (ZD421 · 3×1)" },
];
export const MEDIA_LABEL = Object.fromEntries(MEDIA_TYPES.map((m) => [m.key, m.label]));

// Name-pattern GUESS for a printer nobody has configured yet: if the driver name says
// what it holds, use that until a stock is assigned explicitly. A guess NEVER beats an
// explicit assignment and is always labelled as a guess in the UI.
export const NAME_DEFAULTS = {
  hang_tag: /hang\s*tag/i,
  sticker: /(sticker|4\s*x\s*3|4x3)/i,
  barcode_label: /(zd421|barcode|3\s*x\s*1|3x1)/i,
};

const KEY = "fas_printer_map";
const STOCK_KEY = "fas_printer_stocks";
const NICK_KEY = "fas_printer_nicknames";
const listeners = new Set();

const read = (k) => { try { return JSON.parse(localStorage.getItem(k) || "{}") || {}; } catch { return {}; } };
const write = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* ignore */ } };
const notify = () => listeners.forEach((fn) => fn(getPrinterMap()));

// {mediaKey: printerUid} — the DEFAULT printer for each stock.
export function getPrinterMap() { return read(KEY); }

// {printerUid: mediaKey} — what each printer holds. Legacy configs only had the
// media→printer map, so it is folded in here: its printers hold what they were mapped to.
export function getPrinterStocks() {
  const legacy = {};
  for (const [media, uid] of Object.entries(getPrinterMap())) if (uid) legacy[uid] = media;
  return { ...legacy, ...read(STOCK_KEY) };
}

export function getPrinterNicknames() { return read(NICK_KEY); }

// What this printer holds, and whether that's an operator assignment or a name guess.
export function stockOf(device) {
  if (!device || !device.uid) return { stock: "", source: "none" };
  const assigned = getPrinterStocks()[device.uid];
  if (assigned && MEDIA_LABEL[assigned]) return { stock: assigned, source: "assigned" };
  const guess = (MEDIA_TYPES.find((m) => NAME_DEFAULTS[m.key]?.test(device.name || "")) || {}).key || "";
  return guess ? { stock: guess, source: "guess" } : { stock: "", source: "none" };
}

// Every connected printer that can legally print this media — assigned holders first,
// then name-guessed ones. A printer with no stock is NEVER eligible: it can't be proven
// to match, and guessing is how the wrong layout reaches the wrong stock.
export function printersForMedia(mediaKey, devices) {
  const list = (Array.isArray(devices) ? devices : []).filter((d) => d && d.uid);
  const withStock = list.map((d) => ({ d, s: stockOf(d) })).filter((x) => x.s.stock === mediaKey);
  return [
    ...withStock.filter((x) => x.s.source === "assigned").map((x) => x.d),
    ...withStock.filter((x) => x.s.source === "guess").map((x) => x.d),
  ];
}

// Why this printer may NOT print this layout — a sentence naming BOTH sides and the fix.
// Returns null when the pair is legal. This is the single mismatch rule for the whole app.
export function mediaMismatch(device, mediaKey) {
  if (!device || !mediaKey) return null;
  const { stock } = stockOf(device);
  if (stock === mediaKey) return null;
  const who = printerDisplayName(device);
  const want = MEDIA_LABEL[mediaKey] || mediaKey;
  if (!stock) {
    return `${who} has no stock set, so a ${want} layout can't be sent to it. Nothing was printed. Open Printer Setup and set what stock it holds.`;
  }
  return `${who} holds ${MEDIA_LABEL[stock]} stock — this is a ${want} layout. Nothing was printed. Switch the media to ${MEDIA_LABEL[stock]} to print on that unit, or pick the ${want} printer.`;
}

// Friendly name for any printer — NEVER a bare serial. Priority:
// (1) a nickname you typed in Printer Setup, (2) the STOCK you assigned it, (3) the name
// Browser Print reports IF it reads like a name and not a serial, (4) a guessed stock
// (marked unconfirmed), (5) last resort "Unnamed printer (…1234)".
export function printerDisplayName(d) {
  if (!d) return "—";
  const nick = (getPrinterNicknames()[d.uid] || "").trim();
  if (nick) return nick;
  const { stock, source } = stockOf(d);
  if (source === "assigned") return `${MEDIA_LABEL[stock]} printer`;
  const osName = (d.name || "").trim();
  if (osName && !looksLikeSerial(osName, d.uid)) return osName;
  if (source === "guess") return `${MEDIA_LABEL[stock]} printer (unconfirmed)`;
  return `Unnamed printer (…${String(d.uid || "").slice(-4)})`;
}

// Browser Print reports the SERIAL as the printer name on units with no friendly name set
// ("99n260900260", "99n260900260 (ZT411)"). Never show that to the crew.
export function looksLikeSerial(name, uid) {
  const n = String(name || "").trim();
  if (!n) return true;
  const u = String(uid || "").trim();
  if (u && (n.toLowerCase() === u.toLowerCase() || n.toLowerCase().includes(u.toLowerCase()))) return true;
  return /^[a-z0-9]{8,}$/i.test(n) && /\d/.test(n);   // one unbroken alphanumeric run with digits
}

// Resolve the target printer for a media against a live device list:
// (1) the DEFAULT holder if it's connected, (2) another ASSIGNED holder of the same stock
// (a spare — stock-correct, flagged so the UI can say so), (3) a name-GUESSED holder.
// Returns a device with a `.matchedBy` hint, or null so callers refuse instead of guessing.
export function resolveDeviceForMedia(mediaKey, devices) {
  const eligible = printersForMedia(mediaKey, devices);
  if (!eligible.length) return null;
  const defUid = getPrinterMap()[mediaKey];
  const def = defUid ? eligible.find((d) => d.uid === defUid) : null;
  if (def) return { ...def, matchedBy: "uid" };
  const assigned = eligible.find((d) => stockOf(d).source === "assigned");
  if (assigned) return { ...assigned, matchedBy: defUid ? "spare" : "assigned" };
  return { ...eligible[0], matchedBy: "name" };
}

// ── Writes ─────────────────────────────────────────────────────────────────
// Local only — call savePrinterConfig() to persist. A save that can't reach the server
// must be SHOWN, never swallowed (a nickname that looks saved and silently vanishes is
// worse than a refusal), so persistence is an explicit awaited step at the call site.

// Tell a printer what stock it holds. Many printers may hold the same stock; the first
// one becomes that stock's default. Clearing a stock promotes another holder if there is one.
export function setPrinterStock(uid, mediaKey) {
  if (!uid) return;
  const stocks = read(STOCK_KEY);
  const before = getPrinterStocks()[uid] || "";
  const map = getPrinterMap();
  if (mediaKey) {
    stocks[uid] = mediaKey;
    if (!map[mediaKey]) map[mediaKey] = uid;
  } else {
    delete stocks[uid];
  }
  // legacy map entries are folded into stocks on first write so both stay in step
  for (const [m, u] of Object.entries(map)) if (u && !stocks[u] && u !== uid) stocks[u] = m;
  if (before && before !== mediaKey && map[before] === uid) {
    const other = Object.keys(stocks).find((k) => stocks[k] === before && k !== uid);
    if (other) map[before] = other; else delete map[before];
  }
  write(STOCK_KEY, stocks);
  write(KEY, map);
  notify();
}

// Pick which holder of a stock is the default destination (used when several hold it).
export function setDefaultPrinterFor(mediaKey, uid) {
  if (!mediaKey || !uid) return;
  setPrinterStock(uid, mediaKey);
  const map = getPrinterMap();
  map[mediaKey] = uid;
  write(KEY, map);
  notify();
}

// Back-compat single-step setter: point a stock at a printer (assign + make default).
export function setPrinterFor(mediaKey, uid) {
  if (uid) { setDefaultPrinterFor(mediaKey, uid); return getPrinterMap(); }
  const map = getPrinterMap();
  const old = map[mediaKey];
  delete map[mediaKey];
  write(KEY, map);
  if (old) {
    const stocks = read(STOCK_KEY);
    delete stocks[old];
    write(STOCK_KEY, stocks);
  }
  notify();
  return getPrinterMap();
}

export function setPrinterNickname(uid, name) {
  const m = read(NICK_KEY);
  if (name && name.trim()) m[uid] = name.trim(); else delete m[uid];
  write(NICK_KEY, m);
  notify();
  return m;
}

export function onPrinterMapChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ── Server sync ────────────────────────────────────────────────────────────
// The config lives server-side (UIDs are device serials, stable per printer), so it is set
// once and follows the account to any browser, PC or tablet — and survives a redeploy.
// localStorage is a fast local cache, hydrated on every session boot.
let _hydrated = false;
export async function hydratePrinterConfig(force = false) {
  if (_hydrated && !force) return;
  _hydrated = true;
  try {
    const api = (await import("./api")).default;
    const { data } = await api.get("/tags/printer-config");
    if (data && typeof data === "object") {
      write(KEY, data.map || {});
      write(STOCK_KEY, data.stocks || {});
      write(NICK_KEY, data.names || {});
      notify();
    }
  } catch { _hydrated = false; /* offline/permission — keep local cache */ }
}

// Persist the current config. THROWS on failure so the caller can tell the operator
// plainly instead of showing a save that silently vanishes for everyone else.
export async function savePrinterConfig(extra = {}) {
  const api = (await import("./api")).default;
  await api.put("/tags/printer-config", {
    map: getPrinterMap(), stocks: getPrinterStocks(), names: getPrinterNicknames(), ...extra,
  });
}
